/**
 * CreateRegionModal
 * Modal for creating a new region
 */

import React, { useState } from 'react';
import { Modal, Input, Button, Textarea } from '@/components/ui';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Globe } from 'lucide-react';

interface CreateRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
}

export const CreateRegionModal: React.FC<CreateRegionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
}) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !code.trim()) {
      setError('Name and code are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await addDoc(collection(db, 'regions'), {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        description: description.trim() || null,
        countryIds: [],
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        countryCount: 0,
        locationCount: 0,
        deviceCount: 0,
        modemCount: 0,
      });

      // Reset form
      setName('');
      setCode('');
      setDescription('');
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create region:', err);
      setError(err instanceof Error ? err.message : 'Failed to create region');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setCode('');
      setDescription('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Region"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-status-error-bg border border-status-error/30 rounded-lg">
            <p className="text-sm text-status-error">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Region Name *
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Europe, North America"
            disabled={loading}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Region Code *
          </label>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g., EU, NA"
            maxLength={10}
            disabled={loading}
            required
          />
          <p className="text-xs text-muted-foreground/70 mt-1">
            Short code for the region (will be uppercase)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of the region"
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            disabled={loading || !name.trim() || !code.trim()}
          >
            {loading ? 'Creating...' : 'Create Region'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

